/**
 * `SkillsHealthPanel` — ADR-0129 §2.5, Visibility surface.
 *
 * Compact "Skills" health panel rendered inside the Visibility view as
 * a separate guidance section (NOT folded into the numeric visibility
 * score — deferred per ADR-0129 §2.6 / §2.5). The framing is "how to
 * maximize your store for agents": each skill renders its name + the
 * shared status pill + the same one-line next-step hint Settings uses,
 * so the merchant can read the entire agent-card guarantee surface in
 * one place.
 *
 * Data source is the SAME `api.skills.listForSite` the Settings view
 * uses — no new endpoint, no second derivation. The route's
 * `SkillWithStatus[]` carries the per-skill `status` + `gating_reason`
 * derived from the SAME backing signals the signed card uses (single
 * source of truth — ADR-0129 §2.1).
 *
 * Voice + density (CLAUDE.md §4, §8): thin 1px borders, 2px radii,
 * mono uppercase kicker, no exclamation, no emoji. Pull tokens via
 * `useC()` to keep the dark + light theme symmetry the rest of
 * Visibility honors.
 */

import { useEffect, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';

import type { SiteId, SkillWithStatus } from '@sill/types';

import { useApi } from '../../api/api-context.js';
import { useC } from '../../lib/theme.js';
import {
  skillFulfillmentPathKicker,
  skillHintText,
  skillStatusToPill,
} from '../../lib/skill-status-copy.js';
import { Card, Pill } from '../../components/atoms/index.js';
import type { View } from '../../app/view-router.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export interface SkillsHealthPanelProps {
  readonly site_id: SiteId;
  /** Optional jump callback so the "Manage in Settings" affordance can
   *  route the merchant to the Settings → Skills surface. Threaded
   *  through Visibility's `goTo` nav prop. When omitted, the link is
   *  rendered as inert text (the panel still works in test scaffolds
   *  that don't wire navigation). */
  readonly goTo?: (v: View) => void;
}

export function SkillsHealthPanel({
  site_id,
  goTo,
}: SkillsHealthPanelProps): JSX.Element {
  const C = useC();
  const api = useApi();
  const [skills, setSkills] = useState<readonly SkillWithStatus[] | null>(null);
  const [errored, setErrored] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    setSkills(null);
    setErrored(false);
    api.skills
      .listForSite(site_id)
      .then((items) => {
        if (!cancelled) setSkills(items);
      })
      .catch(() => {
        if (!cancelled) setErrored(true);
      });
    return () => {
      cancelled = true;
    };
  }, [api, site_id]);

  const liveCount = skills?.filter((s) => s.status === 'live').length ?? 0;
  const enabledCount = skills?.filter((s) => s.enabled).length ?? 0;
  const total = skills?.length ?? 0;

  // The `Card` atom doesn't pass through `data-testid`; we wrap it so
  // tests + future regression hooks have a stable surface to query.
  return (
    <div data-testid="visibility-skills-panel">
    <Card className="mt-6">
      <div
        className="px-4 py-3 flex items-center justify-between flex-wrap gap-2"
        style={{ borderBottom: `1px solid ${C.border}` }}
      >
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
          }}
        >
          SKILLS · WHAT AGENTS CAN CALL
        </span>
        {skills !== null && total > 0 && (
          <span
            data-testid="visibility-skills-footer"
            style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}
          >
            {liveCount} live · {enabledCount} enabled · {total} total
          </span>
        )}
      </div>
      {errored ? (
        <div
          className="px-4 py-4"
          style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}
        >
          Couldn&rsquo;t load skill status. Try again shortly.
        </div>
      ) : skills === null ? (
        <div
          className="px-4 py-4"
          style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}
        >
          Loading skill status&hellip;
        </div>
      ) : skills.length === 0 ? (
        <div
          className="px-4 py-4"
          style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}
        >
          No skills configured yet.
        </div>
      ) : (
        <>
          {skills.map((s, i) => {
            const pill = skillStatusToPill(s.status);
            const hint = skillHintText(s.status, s.gating_reason);
            // ADR-0174 §8 — the small "handoff" kicker rendered under
            // place_order's hint when the skill is lit via the WC
            // handoff route (§3 third branch). Absent (null) for every
            // other row + for a settlement-lit place_order — leaves
            // rendering byte-identical for the current shipped cohort.
            // Copy is a marketing-owned placeholder; see the shared
            // helper's docstring.
            const fulfillment_kicker = skillFulfillmentPathKicker(
              s.fulfillment_path,
            );
            return (
              <div
                key={s.skill_id}
                data-testid={`visibility-skill-row-${s.skill_id}`}
                className="flex items-start gap-3 px-4 py-3"
                style={{
                  borderBottom:
                    i < skills.length - 1 ? `1px solid ${C.borderSoft}` : 'none',
                  // Same dimming rule as Settings — disabled rows are
                  // mute; everything else stays at full opacity so the
                  // hint copy reads clearly.
                  opacity: s.status === 'disabled' ? 0.55 : 1,
                }}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span
                      style={{
                        fontFamily: sansFont,
                        fontSize: 13,
                        color: C.text,
                        fontWeight: 500,
                      }}
                    >
                      {s.name}
                    </span>
                    <span
                      data-testid={`visibility-skill-pill-${s.skill_id}`}
                    >
                      <Pill tone={pill.tone}>{pill.label}</Pill>
                    </span>
                    {s.is_custom === true && (
                      <span
                        data-testid={`visibility-skill-custom-${s.skill_id}`}
                      >
                        <Pill tone="neutral">CUSTOM</Pill>
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      fontFamily: monoFont,
                      fontSize: 10,
                      color: C.muted,
                    }}
                  >
                    {s.skill_id}
                  </div>
                  {hint !== null && (
                    <div
                      data-testid={`visibility-skill-hint-${s.skill_id}`}
                      style={{
                        fontFamily: sansFont,
                        fontSize: 12,
                        color: C.muted,
                        marginTop: 4,
                        lineHeight: 1.5,
                      }}
                    >
                      {hint}
                    </div>
                  )}
                  {fulfillment_kicker !== null && (
                    <div
                      data-testid={`visibility-skill-fulfillment-kicker-${s.skill_id}`}
                      style={{
                        fontFamily: monoFont,
                        fontSize: 10,
                        color: C.muted,
                        marginTop: 4,
                        letterSpacing: 0.3,
                        textTransform: 'uppercase',
                      }}
                    >
                      {fulfillment_kicker}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {goTo !== undefined && (
            <div
              className="px-4 py-3 flex justify-end"
              style={{ borderTop: `1px solid ${C.borderSoft}` }}
            >
              <button
                type="button"
                onClick={() => {
                  // Deep-link into Settings' Skills tab via the same
                  // `?settings_tab=` one-shot channel SitesList's
                  // "Review origins" band uses (Settings.tsx reads +
                  // strips it on mount). Without this, `goTo('settings')`
                  // alone always lands on the General tab.
                  if (typeof window !== 'undefined') {
                    try {
                      const url = new URL(window.location.href);
                      url.searchParams.set('settings_tab', 'skills');
                      window.history.replaceState({}, '', url.toString());
                    } catch {
                      // Best-effort; Settings defaults to General on
                      // parse failure.
                    }
                  }
                  goTo('settings');
                }}
                data-testid="visibility-skills-manage"
                aria-label="Manage skills in Settings"
                className="flex items-center gap-1 px-2.5 py-1"
                style={{
                  background: C.surface2,
                  color: C.text,
                  border: `1px solid ${C.border}`,
                  fontFamily: sansFont,
                  fontSize: 11,
                  fontWeight: 500,
                  borderRadius: 2,
                  cursor: 'pointer',
                }}
              >
                Manage in Settings <ArrowUpRight size={10} />
              </button>
            </div>
          )}
        </>
      )}
    </Card>
    </div>
  );
}
